import { darken } from 'polished'
import { useMemo } from 'react'
import { Activity } from 'react-feather'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { useAccount, useChainId } from 'wagmi'
import useENSName from '../../hooks/useENSName'
import { useWalletModalToggle } from '../../state/application/hooks'
import { isTransactionRecent, useAllTransactions } from '../../state/transactions/hooks'
import { TransactionDetails } from '../../state/transactions/reducer'
import { shortenAddress } from '../../utils'
import { ButtonPrimary } from '../Button'

import Identicon from '../Identicon'
import Loader from '../Loader'

import WalletModal from '../WalletModal'
import { SUPPORTED_CHAIN_IDS } from '../../wagmi'

// Type helper for checking chain support
const isSupportedChain = (chainId: number | undefined): boolean => {
  if (chainId === undefined) return false
  return (SUPPORTED_CHAIN_IDS as readonly number[]).includes(chainId)
}

const _IconWrapper = styled.div<{ $size?: number }>`
  ${({ theme }) => theme.flexColumnNoWrap};
  align-items: center;
  justify-content: center;
  & > * {
    height: ${({ $size }) => ($size ? $size + 'px' : '32px')};
    width: ${({ $size }) => ($size ? $size + 'px' : '32px')};
  }
`

const Web3StatusGeneric = styled(ButtonPrimary)`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  align-items: center;
  padding: 0.5rem;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  :focus {
    outline: none;
  }
`
const Web3StatusError = styled(Web3StatusGeneric)`
  background-color: ${({ theme }) => theme.red1};
  border: 1px solid ${({ theme }) => theme.red1};
  color: ${({ theme }) => theme.white};
  font-weight: 500;
  :hover,
  :focus {
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
`

const Web3StatusConnect = styled(Web3StatusGeneric)<{ $faded?: boolean }>`
  background-color: ${({ theme }) => theme.green1};
  border: none;
  color: ${({ theme }) => theme.black1};
  font-weight: 600;

  /* Ensure text inside inherits dark color */
  p, span {
    color: ${({ theme }) => theme.black1};
  }

  :hover,
  :focus {
    background-color: ${({ theme }) => darken(0.05, theme.green1)};
    border: 1px solid ${({ theme }) => darken(0.05, theme.green1)};
    color: ${({ theme }) => theme.black1};
  }

  ${({ $faded }) =>
    $faded &&
    css`
      background-color: ${({ theme }) => theme.green1};
      border: 1px solid ${({ theme }) => theme.green1};
      color: ${({ theme }) => theme.black1};

      p, span {
        color: ${({ theme }) => theme.black1};
      }

      :hover,
      :focus {
        background-color: ${({ theme }) => darken(0.05, theme.green1)};
        border: 1px solid ${({ theme }) => darken(0.05, theme.green1)};
        color: ${({ theme }) => theme.black1};
      }
    `}
`

const Web3StatusConnected = styled(Web3StatusGeneric)<{ $pending?: boolean }>`
  background-color: ${({ $pending, theme }) => ($pending ? theme.primary1 : theme.bg1)} !important;
  background: ${({ $pending, theme }) => ($pending ? theme.primary1 : theme.bg1)} !important;
  border: 1px solid ${({ $pending, theme }) => ($pending ? theme.primary1 : theme.bg3)} !important;
  color: ${({ $pending, theme }) => ($pending ? theme.green1 : theme.text1)} !important;
  font-weight: 500;
  gap: 8px;
  padding: 8px 12px;
  display: inline-flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  width: auto;
  transition: all 0.15s ease;
  box-shadow: none !important;

  /* Hide any injected wallet icons/avatars */
  & > img,
  & > div > img,
  & [class*="avatar"],
  & [class*="Avatar"],
  & wui-avatar,
  & w3m-avatar {
    display: none !important;
  }

  &:hover,
  &:focus {
    background-color: ${({ $pending, theme }) => ($pending ? darken(0.05, theme.primary1) : theme.bg1)} !important;
    background: ${({ $pending, theme }) => ($pending ? darken(0.05, theme.primary1) : theme.bg1)} !important;
    border-color: ${({ theme }) => theme.green1} !important;
    transform: none !important;
    box-shadow: none !important;
  }
`

const Text = styled.p`
  flex: 0 0 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: inherit;
`

const NetworkIcon = styled(Activity)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
`

// we want the latest one to come first, so return negative if a is after b
function newTransactionsFirst(a: TransactionDetails, b: TransactionDetails) {
  return b.addedTime - a.addedTime
}

function Web3StatusInner() {
  const { t } = useTranslation()
  const { address: account, isConnected } = useAccount()
  const chainId = useChainId()

  const { ENSName } = useENSName(account ?? undefined)

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter(tx => !tx.receipt).map(tx => tx.hash)

  const hasPendingTransactions = !!pending.length
  const toggleWalletModal = useWalletModalToggle()

  // Check if connected to wrong network
  const isWrongNetwork = isConnected && chainId && !isSupportedChain(chainId)

  if (account && isConnected) {
    if (isWrongNetwork) {
      return (
        <Web3StatusError onClick={toggleWalletModal}>
          <NetworkIcon />
          <Text>Wrong Network</Text>
        </Web3StatusError>
      )
    }
    return (
      <Web3StatusConnected id="web3-status-connected" onClick={toggleWalletModal} $pending={hasPendingTransactions}>
        {hasPendingTransactions ? (
          <>
            <Text>{pending?.length} Pending</Text>
            <Loader stroke="#3BE891" />
          </>
        ) : (
          <>
            <Text>{ENSName || shortenAddress(account)}</Text>
          </>
        )}
        {!hasPendingTransactions && <Identicon />}
      </Web3StatusConnected>
    )
  } else {
    return (
      <Web3StatusConnect id="connect-wallet" onClick={toggleWalletModal} $faded={!account}>
        <Text>{t('Connect to a wallet')}</Text>
      </Web3StatusConnect>
    )
  }
}

export default function Web3Status() {
  const { address: account } = useAccount()

  const { ENSName } = useENSName(account ?? undefined)

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter(tx => !tx.receipt).map(tx => tx.hash)
  const confirmed = sortedRecentTransactions.filter(tx => tx.receipt).map(tx => tx.hash)

  return (
    <>
      <Web3StatusInner />
      <WalletModal ENSName={ENSName ?? undefined} pendingTransactions={pending} confirmedTransactions={confirmed} />
    </>
  )
}
